import React, { useMemo } from 'react';
import moment from 'moment';
import { useAureliaI18n } from '../../../react-hooks/useAureliaI18n';
import { formatCurrency } from '../../../toma-mask/fast-booking/components/functions/fucntions';
import ChoiceLabel from './helpers/ChoiceLabel';

const getColumns = (value) => {
    const columns = new Map();

    for (const priceLine of value.priceLines ?? []) {
        if (!priceLine.countFrom || !priceLine.countTo) {
            continue;
        }

        const key = `${priceLine.countFrom}-${priceLine.countTo}`;

        if (!columns.has(key)) {
            columns.set(key, {
                min: priceLine.countFrom,
                max: priceLine.countTo,
            });
        }
    }

    return Array.from(columns.values()).sort((a, b) => a.min - b.min);
};

const getPriceLines = (value, columns) => {
    const priceLines = new Map();

    for (const priceLine of value.priceLines ?? []) {
        if (!priceLine.countFrom || !priceLine.countTo) {
            continue;
        }

        const index = columns.findIndex(
            (column) =>
                column.min === priceLine.countFrom && column.max === priceLine.countTo
        );

        if (index === -1) {
            continue;
        }

        const key = `${priceLine.from}-${priceLine.to}`;
        let entry = priceLines.get(key);

        if (!entry) {
            entry = {
                from: priceLine.from,
                to: priceLine.to,
                seasonText: priceLine.seasonText,
                prices: [],
            };
        }

        entry.prices[index] = {
            retailPrice: priceLine.retailPrice,
            purchasePrice: priceLine.purchasePrice,
        };

        priceLines.set(key, entry);
    }

    return Array.from(priceLines.values());
};

const DateRangeCell = ({ from, to, seasonText }) => (
    <>
        {seasonText != null && seasonText !== '' && (
            <>
                <i>{seasonText}:</i>
                <br />
            </>
        )}
        {moment(from).format('L')} - {moment(to).format('L')}
    </>
);

const PriceSet = ({ params }) => {
    const value = params?.value;
    const { t } = useAureliaI18n();

    const columns = useMemo(() => {
        if (value?.type === 'dateAndCount' || value?.type === 'count') {
            return getColumns(value);
        }
        return [];
    }, [value]);

    const priceLines = useMemo(() => {
        if (value?.type === 'dateAndCount' || value?.type === 'count') {
            return getPriceLines(value, columns);
        }
        return [];
    }, [value, columns]);

    if (!value) {
        return <></>;
    }

    return (
        <>
            {value.type === 'single' && (
                <table className="table table-condensed table-hover">
                    <tbody>
                        <tr>
                            <td style={{ width: '33%' }}>{t('sio.field.purchasePrice')}</td>
                            <td>{formatCurrency(value.purchasePrice)}</td>
                        </tr>
                        <tr>
                            <td style={{ width: '33%' }}>{t('sio.field.retailPrice')}</td>
                            <td>{formatCurrency(value.retailPrice)}</td>
                        </tr>
                    </tbody>
                </table>
            )}

            {(value.type === 'date' || value.type === 'dateAndWeekDay') && (
                <div className="table-responsive">
                    <table className="table table-condensed table-hover">
                        <thead>
                            <tr>
                                <th>{t('sio.field.date')}</th>
                                {value.type === 'dateAndWeekDay' && (
                                    <th>{t('sio.field.weekDays')}</th>
                                )}
                                <th>{t('sio.field.purchasePrice')}</th>
                                <th>{t('sio.field.retailPrice')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {value.priceLines?.map((priceLine, index) => (
                                <tr key={index}>
                                    <td style={{ width: '33%' }}>
                                        <DateRangeCell
                                            from={priceLine.from}
                                            to={priceLine.to}
                                            seasonText={priceLine.seasonText}
                                        />
                                    </td>
                                    {value.type === 'dateAndWeekDay' && (
                                        <td>
                                            <div className="list-without-space">
                                                {priceLine.weekDays?.map((weekday, weekdayIndex) => (
                                                    <React.Fragment key={weekdayIndex}>
                                                        <ChoiceLabel
                                                            set="dayOfWeekAbbreviation"
                                                            value={weekday}
                                                        />
                                                        {weekdayIndex < priceLine.weekDays.length - 1 && (
                                                            <span className="separator">, </span>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                        </td>
                                    )}
                                    <td>{formatCurrency(priceLine.purchasePrice)}</td>
                                    <td>{formatCurrency(priceLine.retailPrice)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {(value.type === 'dateAndCount' || value.type === 'count') && (
                <div className="table-responsive">
                    <table className="table table-condensed table-hover">
                        <thead>
                            <tr>
                                {value.type === 'dateAndCount' && <th>{t('sio.field.date')}</th>}
                                <th />
                                {columns.map((column, index) => (
                                    <th key={index}>
                                        {column.min} - {column.max}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {priceLines.map((priceLine, lineIndex) => (
                                <tr key={lineIndex}>
                                    {value.type === 'dateAndCount' && (
                                        <td style={{ width: '33%' }}>
                                            <DateRangeCell
                                                from={priceLine.from}
                                                to={priceLine.to}
                                                seasonText={priceLine.seasonText}
                                            />
                                        </td>
                                    )}
                                    <td>
                                        EK
                                        <br />
                                        VK
                                    </td>
                                    {columns.map((column, columnIndex) => (
                                        <td key={columnIndex}>
                                            {priceLine.prices[columnIndex]?.purchasePrice &&
                                                formatCurrency(
                                                    priceLine.prices[columnIndex].purchasePrice
                                                )}
                                            <br />
                                            {priceLine.prices[columnIndex]?.retailPrice &&
                                                formatCurrency(priceLine.prices[columnIndex].retailPrice)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {value.pricePerCount && <i>{t('price.pricePerCount')}</i>}
        </>
    );
};

export default PriceSet;
